/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {KeyValue} from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import {
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {generateUuid} from "@app/models/functions/generate-uuid.function";
import {CommonState} from "@app/store/common/common.state";
// eslint-disable-next-line max-len
import {
  ALARM_ANTICIPATION_UNIT,
  ALARM_TYPE,
  EXAM_PERIODIC_UNIT,
  ListItem,
  TASK_CLINICAL_DATA_TYPES,
  TASK_CLINICAL_DATA_TYPES_CARDIO,
  TASK_CLINICAL_DATA_TYPES_CDCD,
  TASK_CLINICAL_DATA_TYPES_ONCO,
  TASK_CLINICAL_DATA_TYPES_PARKINSON,
  TASK_CLINICAL_DATA_TYPES_PERIOPERATIVO,
  TASK_CLINICAL_DATA_TYPES_PNEUMO,
  TASK_CLINICAL_DATA_TYPES_PREGNANCY,
  TASK_CLINICAL_DATA_TYPES_STOMACHCARE,
  TASK_CLINICAL_DATA_TYPES_WOUNDCARE,
} from "@doctor-panel-modules/patient-treatment-plan/models/treatment-plan-enums";
import {environment} from "@env/environment";
import {Store} from "@ngxs/store";
// eslint-disable-next-line max-len
// MatDialog and custom TimePicker removed
import {
  ClinicalDataService,
  ClinicalDataType,
  TrecAlarmData,
  TrecAlarmType,
  TrecAssumption,
  TrecExecutionData,
  TrecMoment,
  TrecTask,
} from "@trec/api/clinical-data";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {addDays, addMonths, endOfDay, startOfDay} from "date-fns";
import {BehaviorSubject, Subscription} from "rxjs";
import {CdItemFormComponent, CdItemFormMode} from "../cd-item-form-component";

interface FormTaskData {
  startDate: Date | null;
  endDate: Date | null; // Date
  measurementType: ClinicalDataType | null;

  notes: string | null;

  frequencyType: /*'at_need' |*/ "every_x_days";
  frequencyValue: number | null;
  frequencyUnit: "year" | "month" | "week" | "day";

  alarm: TrecAlarmData | null;

  executions: TrecExecutionData[];
}

interface Prescription {
  doctor: string | undefined;
  structure: string | undefined;
  state: string | undefined;
  date: string | undefined;
}

type FormTaskDataKeys = {[key in keyof FormTaskData]: any};

@Component({
  selector: "c3-form-task",
  templateUrl: "./form-task.component.html",
  styleUrls: ["./form-task.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormTaskComponent
  extends CdItemFormComponent<TrecTask, FormTaskData>
  implements OnDestroy {
  // IO ///////////////////////////////////////////////////////////////////////////////////////////
  @Input() AIFlag = false;
  @Input()
  public get mode(): CdItemFormMode {
    return super.mode;
  }
  public set mode(value: CdItemFormMode) {
    super.mode = value;
  }

  @Input()
  public get item(): TrecTask | undefined {
    return super.item;
  }
  public set item(value: TrecTask | undefined) {
    super.item = value;
  }
  @Input()
  public get context(): RegistryContexts[] | undefined | null {
    //console.log(' get context');
    return this._context!;
  }
  public set context(value: RegistryContexts[] | undefined | null) {
    //console.log('set context');

    this._context = value;
    // Update form
    this._update$.next(true);

    if (
      this.context?.includes(RegistryContexts.CARDIO) ||
      this.context?.includes(RegistryContexts.CARDIO_DIABETES)
    ) {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_CARDIO);
    } else if (
      this.context?.includes(RegistryContexts.ONCOLOGY) ||
      this.context?.includes(RegistryContexts.ONCOLOGY_BREAST_CANCER)
    ) {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_ONCO);
    } else if (this.context?.includes(RegistryContexts.PREGNANCY)) {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_PREGNANCY);
    } else if (this.context?.includes(RegistryContexts.PARKINSON)) {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_PARKINSON);
    } else if (
      this.context?.includes(RegistryContexts.PERIOPERATIVE) ||
      this.context?.includes(RegistryContexts.WOUNDCARE) ||
      this.context?.includes(RegistryContexts.STOMACHCARE)
    ) {
      // Gestione contesti chirurgici: combina i task se presenti più contesti
      const surgicalContexts = [
        this.context?.includes(RegistryContexts.PERIOPERATIVE),
        this.context?.includes(RegistryContexts.WOUNDCARE),
        this.context?.includes(RegistryContexts.STOMACHCARE),
      ].filter(Boolean).length;

      if (surgicalContexts > 1) {
        // Più contesti chirurgici: combina tutti i task evitando duplicati
        const combinedTypes: ListItem[] = [];
        const seenKeys = new Set<string>();

        const addUniqueTypes = (types: ListItem[]) => {
          types.forEach((type) => {
            if (!seenKeys.has(type.key)) {
              seenKeys.add(type.key);
              combinedTypes.push(type);
            }
          });
        };

        if (this.context?.includes(RegistryContexts.PERIOPERATIVE)) {
          addUniqueTypes(TASK_CLINICAL_DATA_TYPES_PERIOPERATIVO);
        }
        if (this.context?.includes(RegistryContexts.WOUNDCARE)) {
          addUniqueTypes(TASK_CLINICAL_DATA_TYPES_WOUNDCARE);
        }
        if (this.context?.includes(RegistryContexts.STOMACHCARE)) {
          addUniqueTypes(TASK_CLINICAL_DATA_TYPES_STOMACHCARE);
        }
        this.availableCdTypes$.next(combinedTypes);
      } else {
        // Singolo contesto chirurgico
        if (this.context?.includes(RegistryContexts.PERIOPERATIVE)) {
          this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_PERIOPERATIVO);
        } else if (this.context?.includes(RegistryContexts.STOMACHCARE)) {
          this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_STOMACHCARE);
        } else if (this.context?.includes(RegistryContexts.WOUNDCARE)) {
          this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_WOUNDCARE);
        }
      }
    } else if (this.context?.includes(RegistryContexts.CDCD)) {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_CDCD);
    } else if (this.context?.includes(RegistryContexts.PNEUMOLOGIA)) {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES_PNEUMO);
    } else {
      this.availableCdTypes$.next(TASK_CLINICAL_DATA_TYPES);
    }
  }

  /**
   * Flag to decide whether moment of day is required or not.
   */
  private get momentRequired() {
    // Required only if measurement type is glycaemia
    return this.form.get("measurementType")?.value === ClinicalDataType.GLYCAEMIA;
  }

  /**
   * Assumption form array from main form.
   */
  get executionsControls(): UntypedFormArray {
    return this.form.get("executions") as UntypedFormArray;
  }

  prescription: Prescription[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private c3SnackbarStatus: C3SnackbarStatusService,
    cd: ChangeDetectorRef,
    private cdService: ClinicalDataService,
    @Inject(LOCALE_ID) private localeId: string,
  ) {
    super(cd);

    // Init list of moments (exclude "at_need")
    const {["at_need"]: _, ...moments} = TrecAssumption.TRANSLATION[this.localeId]["moment"];
    this.DAY_MOMENTS = moments;

    // Initialize form schema
    this.form = this.fb.group({
      startDate: [this.today, Validators.required], // Date
      endDate: [addDays(this.today, 30), Validators.required], // Date
      measurementType: [null, Validators.required],
      frequencyType: ["every_x_days", Validators.required],
      frequencyUnit: ["day", Validators.required],
      frequencyValue: [1, [Validators.required, Validators.min(1)]],
      alarm: this.fb.group({
        type: ["notification", Validators.required],
        anticipation_value: [10, Validators.required],
        anticipation_unit: ["minute", Validators.required],
      }),
      executions: this.fb.array([]),
      notes: [""],
    } as FormTaskDataKeys);

    // Add an empty default execution
    this.onAddExecution();
  }

  // Utils ////////////////////////////////////////////////////////////////////////////////////////

  get normalizedEndDate(): Date {
    const freqFlag = this.formValue.frequencyType === "every_x_days";
    if (freqFlag) {
      if (!this.formValue.endDate) {
        throw new Error(
          `Bad exam's endDate. endDate is missing but frequency has been selected (${this.formValue.frequencyType})`,
        );
      }
      return endOfDay(this.formValue.endDate);
    } else {
      // Return "infinity"
      // return new Date(this.MAXIMUM_DATE_UNIX);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return endOfDay(this.formValue.startDate!);
    }
  }

  get normalizedFrequencyValue(): number {
    switch (this.formValue.frequencyUnit) {
      case "year":
        return (this.formValue.frequencyValue || 0) * 365;
      case "month":
        return (this.formValue.frequencyValue || 0) * 30;
      case "week":
        return (this.formValue.frequencyValue || 0) * 7;
      case "day":
        return this.formValue.frequencyValue || 0;
      default:
        throw new Error(`Unknown task frequencyUnit "${this.formValue.frequencyUnit}"`);
    }
  }

  @Input()
  actionPending: boolean;

  /**
   * Whether it's necessary to add at least one dosage or not.
   */
  private _context?: RegistryContexts[] | undefined | null;

  // Component elems /////////////////////////////////////////////////////////////////////////////

  @ViewChild("formElem", {read: FormGroupDirective, static: true}) formElem: FormGroupDirective;

  // Defaults and Enums ///////////////////////////////////////////////////////////////////////////

  readonly DAY_MOMENTS: {[key: string]: string}; // Initialized in constructor
  readonly TASK_CLINICAL_DATA_TYPES = TASK_CLINICAL_DATA_TYPES;
  readonly ALARM_ANTICIPATION_UNIT = ALARM_ANTICIPATION_UNIT;
  readonly ALARM_TYPE = ALARM_TYPE;
  readonly EXAM_PERIODIC_UNIT = EXAM_PERIODIC_UNIT;
  readonly MAXIMUM_DATE_UNIX = 8630000000000000;
  readonly today = new Date();

  readonly availableCdTypes$ = new BehaviorSubject<ListItem[]>(TASK_CLINICAL_DATA_TYPES);

  // Form fields //////////////////////////////////////////////////////////////////////////////////

  readonly form: UntypedFormGroup;

  private readonly subscr = new Subscription();

  /**
   * Function used to sort entries by value, alphabetically.
   */
  readonly sortAlphabetically = (a: KeyValue<string, string>, b: KeyValue<string, string>) =>
    a.value > b.value ? 1 : -1;

  ngOnInit() {
    // Subscribe for form values changes
    this.subscr.add(
      // Alarm
      (this.form.get("alarm.type") as UntypedFormControl).valueChanges.subscribe(
        (alarmType: TrecAlarmType | null) => {
          if (alarmType && alarmType === "none") {
            (this.form.get("alarm.anticipation_value") as UntypedFormControl).patchValue(0, {
              emitEvent: false,
            });
            (this.form.get("alarm.anticipation_unit") as UntypedFormControl).patchValue("minute", {
              emitEvent: false,
            });
          }
        },
      ),
    );

    // Start Date
    this.subscr.add(
      (this.form.get("startDate") as UntypedFormControl).valueChanges.subscribe((startDate) => {
        // Change end date accordingly
        const endDateControl = this.form.get("endDate") as UntypedFormControl;
        if (endDateControl && endDateControl.value <= startDate) {
          endDateControl.patchValue(addDays(startDate ?? new Date(), 30));
        }
      }),
    );

    // Listen for measurementType changes
    this.subscr.add(
      (this.form.get("measurementType") as UntypedFormControl).valueChanges.subscribe(() => {
        // Update the moment of the day validators (changes depending on the selected cd type)
        this.updateMomentValidators();
      }),
    );
    if (this.item) {
      this.cdService.getStoricoTask(this.item.id!).subscribe(
        (data: any) => {
          for (let i = 0; i < data.length; i++) {
            let stato;
            switch (data[i].action) {
              case "inserimento":
                stato = "prescritto";
                break;
              case "modifica":
                stato = "modificato";
                break;
              case "cancellazione":
                stato = "cancellato";
                break;
              case "sospensione":
                stato = "sospeso";
                break;
              case "riattivazione":
                stato = "riattivato";
            }
            this.prescription[i] = {
              doctor: data[i].doctor.name,
              structure: data[i].structure,
              date: data[i].date,
              state: stato,
            };
          }
          //console.log(this.prescription);
          this.cd.detectChanges();
        },
        (err: any) => {
          console.error(err);
        },
      );
    }
  }

  protected resetDefaultValues() {
    const defaultFreq = this.getDefaultFreq(this.context);

    // Set default values
    this.form.patchValue({
      startDate: this.today,
      endDate: this.getDefaultPeriodicity(this.context),
      measurementType:
        this.availableCdTypes$.getValue().length === 1
          ? this.availableCdTypes$.getValue()[0].key // Immediately select unique available value
          : null, // Select nothing
      frequencyType: defaultFreq.frequencyType,
      frequencyUnit: defaultFreq.frequencyUnit,
      frequencyValue: defaultFreq.frequencyValue,
      alarm: {
        type: "notification",
        anticipation_value: 10,
        anticipation_unit: "minute",
      },
      executions: [],
      notes: "",
    } as FormTaskDataKeys);

    // Add a default execution
    this.onAddExecution();
  }

  // Callbacks ////////////////////////////////////////////////////////////////////////////////////

  onAddExecution() {
    this.executionsControls.push(
      this.fb.group({
        _id: [generateUuid(), Validators.required],
        moment: [null, this.momentRequired ? Validators.required : undefined],
        time: [null, Validators.required],
      } as {[key in keyof TrecExecutionData]: any}),
    );
  }

  onRemoveExecution(index: number) {
    if (this.executionsControls.length > 1) {
      this.executionsControls.removeAt(index);
    } else {
      this.c3SnackbarStatus.showInfo({message: `È necessario mantenere almeno un'esecuzione`});
    }
  }

  prepareData(): TrecTask | null {
    if (this.form.invalid) {
      // Check validity
      return null;
    }

    // Convert form into task
    const isEditMode = this.mode === "edit";

    if (isEditMode && !this.item) {
      throw new Error(`Trying to submit editing of a task but task data are unknown`);
    }

    const task = TrecTask.create(
      {
        id: isEditMode ? this.item!.id : undefined, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        patientId: isEditMode
          ? this.item!.patientId || ""
          : this.store.selectSnapshot(CommonState.patientId) || "",
      },
      {
        start_date: startOfDay(this.formValue.startDate!).valueOf(), // eslint-disable-line @typescript-eslint/no-non-null-assertion
        end_date: this.normalizedEndDate.valueOf(),
        source: isEditMode ? this.item!.metadata.source : environment.appId, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        provenance: isEditMode ? this.item!.metadata.provenance : "medical", // eslint-disable-line @typescript-eslint/no-non-null-assertion
        device: isEditMode ? this.item!.metadata.device : undefined, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        doctor: {
          id: this.store.selectSnapshot(CommonState.userId)!,
          name:
            this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.surname! +
            " " +
            this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.name,
        },
        structure: this.store.selectSnapshot(CommonState.currentStructureId)!,
      },
      {
        data_type: this.formValue.measurementType!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        alarm: this.formValue.alarm!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        regular: {
          executions:
            this.formValue.executions?.map((exec) => ({
              ...exec,
              moment: exec.moment ?? undefined,
            })) ?? [],
          frequency: {
            type: this.formValue.frequencyType,
            values: [this.normalizedFrequencyValue],
            // values: this.formValue.frequencyType !== 'at_need' ? [this.normalizedFrequencyValue] : []
          },
        },
        variable: {
          executions: [],
        },
        notes: this.formValue.notes ?? "",
      },
    );

    return task;
  }

  update() {
    super.update();

    // Reset executions controls
    this.form.setControl("executions", this.fb.array([]));

    if (this.item) {
      const {data, metadata} = this.item;
      // Prepare executions controls
      this.form.setControl(
        "executions",
        this.fb.array(
          data.regular.executions.map((e) =>
            this.fb.group({
              _id: [e._id, Validators.required],
              moment: [
                e.moment as TrecMoment,
                this.momentRequired ? Validators.required : undefined,
              ],
              time: [e.time, Validators.required],
            }),
          ),
        ),
      );

      // Set new values
      this.form.patchValue({
        startDate: new Date(metadata.start_date),
        endDate: new Date(metadata.end_date),
        measurementType: data.data_type,
        frequencyType: data.regular.frequency.type,
        frequencyUnit: "day",
        frequencyValue: data.regular.frequency.values ? data.regular.frequency.values[0] : null,
        alarm: data.alarm,
        executions: data.regular.executions,
        notes: data.notes || "",
      } as FormTaskDataKeys);
    } else {
      this.resetDefaultValues();
    }

    this.updateMomentValidators();

    if (this.AIFlag) {
      console.log("AIFlag attivo - controllo campi form");
      // 👇 Controllo dei campi valorizzati
      const allFieldsFilled =
        this.form.get("startDate")?.value &&
        this.form.get("endDate")?.value &&
        this.form.get("measurementType")?.value &&
        this.form.get("frequencyType")?.value &&
        this.form.get("frequencyUnit")?.value &&
        this.form.get("frequencyValue")?.value !== null &&
        this.form.get("alarm")?.value &&
        this.form.get("executions")?.value?.length > 0; // se executions è array

      if (allFieldsFilled) {
        this.form.markAsDirty();
        this.form.markAsTouched();
        //console.log('👉 Form marcata come pristine');
      } else {
        //console.log('⚠️ Mancano alcuni campi');
      }
    }

    // Enable/disable executions (this does not happen when form is enabled/disabled)
    if (this.isViewMode) {
      this.executionsControls.disable();
    } else {
      this.executionsControls.enable();
    }
  }

  private getDefaultFreq(ctx: RegistryContexts[] | undefined | null): {
    frequencyType: /*'at_need' |*/ "every_x_days";
    frequencyValue: number | null;
    frequencyUnit: "month" | "week" | "day";
  } {
    if (ctx?.includes(RegistryContexts.CARDIO) || ctx?.includes(RegistryContexts.CARDIO_DIABETES)) {
      return {
        frequencyType: "every_x_days",
        frequencyUnit: "day",
        frequencyValue: 1,
      };
    } else {
      return {
        frequencyType: "every_x_days",
        frequencyUnit: "day",
        frequencyValue: 1,
      };
    }
  }

  private getDefaultPeriodicity(ctx: RegistryContexts[] | undefined | null): Date {
    if (ctx?.includes(RegistryContexts.CARDIO_DIABETES)) {
      return addDays(addMonths(this.today, 6), 1);
    } else {
      return addDays(this.today, 30);
    }
  }

  private updateMomentValidators() {
    const isRequired = this.momentRequired;

    this.executionsControls.controls.forEach((control) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const momentControl = control.get("moment")!;

      if (isRequired) {
        // Add to existing execution's moments the required validator
        momentControl.setValidators([Validators.required]);
      } else {
        // Remove to existing execution's moments the required validator
        momentControl.setValidators([]);
      }

      momentControl.updateValueAndValidity();
    });
  }

  getClassOf(val: any) {
    {
      switch (val) {
        case "prescritto":
          return "green";
        case "modificato":
          return "yellow";
        case "cancellato":
          return "red";
        case "sospeso":
          return "orange";
        case "riattivato":
          return "blue";
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.subscr.unsubscribe();
    this.availableCdTypes$.complete();
  }
}
